'use client';

import { useEffect, useMemo, useState } from 'react';
import TableV2 from '../Tables/TableV2';
import { UISerializableSwapRecord } from '@drift/common';
import SwapHistoryTableRow from '../Swap/SwapHistoryTableRow';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import { HistoryTableUIProps } from 'src/hooks/historyData/historyDataTypes';
import { useSwapsHistory } from 'src/hooks/historyData/useSwapsHistory';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import SwapHistoryTableMobileCard from 'src/components/Swap/SwapHistoryTableMobileCard';
import useExpandableItemList from 'src/hooks/lists/expandable/useExpandableItemList';
import ExpandAllButton from 'src/components/Buttons/ExpandAllButton';
import { cm } from 'src/utils/classNames';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';

const PAGE_SIZE = 20;
// Empty header for view txn icon column.
const HEADERS = ['Time', 'Swap', 'Swap Price', ''];
const TABLE_GRID =
	'minmax(360px, 1fr) minmax(230px, 1fr) minmax(164px, 1fr) 40px';

interface V2SwapHistoryTableDisplayProps extends HistoryTableUIProps<UISerializableSwapRecord> {
	isLive?: boolean;
}

const V2SwapHistoryTableDisplay = (props: V2SwapHistoryTableDisplayProps) => {
	const setHistoryTableFilterState = useHistoryTableFilterStore((s) => s.set);
	const filters = useHistoryTableFilterStore((s) => s.getFilterState('swaps'));
	const currentPage = filters.currentUiPage;
	const isMobileScreenSize = useIsMobileScreenSize();

	const { toggleDateDisplayType, displayDatesAsTimestamp } =
		useDateDisplayState();
	useWalletIsConnected({
		onDisconnectedCallback: resetState,
	});

	const { historyData } = props;
	const swapHistory = historyData.records;
	const loading = historyData.loading;
	const totalCount = historyData.totalCount;

	const [currentSwapsHistory, setCurrentSwapsHistory] = useState(
		swapHistory?.slice(0, PAGE_SIZE) ?? undefined // undefined state needed for loading state in TableStateWrapper
	);
	const [isSellDirection, setIsSellDirection] = useState(false);

	const filteredSwaps = useMemo(() => {
		return props.filterFn
			? swapHistory?.filter((record) =>
					props.filterFn(record, filters, { isLive: props.isLive })
				)
			: swapHistory;
	}, [swapHistory, props.filterFn, filters]);

	const maxNumOfRecordsToDisplay =
		filters.getFilterMode() === 'SHORT_TERM'
			? totalCount // short term data API returns pages of 20 records up to the current page, and we do a workaround to (+1 to total count) when we know there is a next page
			: (filteredSwaps?.length ?? 0); // long term data API returns up to 5000 records for the first page, so we check the actual displayed records (filtered records) length

	useEffect(() => {
		setCurrentSwapsHistory(
			filteredSwaps?.slice(
				currentPage * PAGE_SIZE,
				(currentPage + 1) * PAGE_SIZE
			) ?? undefined
		);
	}, [filteredSwaps, currentPage]);

	function resetState() {
		setCurrentSwapsHistory([]);
		setHistoryTableFilterState((s) => {
			s.swaps.currentUiPage = 0;
		});
	}

	const {
		areAllItemsExpanded,
		handleItemExpandedChange,
		handleExpandAllItems,
		handleCollapseAllItems,
	} = useExpandableItemList({
		tableId: 'swap-history',
		numItems: currentSwapsHistory?.length ?? 0,
	});

	return (
		<HistoryTable
			tableGrid={TABLE_GRID}
			pageSize={PAGE_SIZE}
			totalItems={maxNumOfRecordsToDisplay}
			currentPage={currentPage}
			pageChangeCallback={(newPage) =>
				props.executePageChange(newPage, PAGE_SIZE)
			}
			loading={loading}
			tableState={{
				records: currentSwapsHistory,
				id: 'swap_history_table',
			}}
			tableContentTop={
				<TableV2.HeaderRow grid={TABLE_GRID}>
					{HEADERS.map((header) => (
						<TableV2.HeaderCell key={header}>
							{header.toLowerCase() === 'time' ? (
								<ToggleHeaderCell
									label={header}
									onClick={toggleDateDisplayType}
								/>
							) : header.toLowerCase() === 'swap price' ? (
								<ToggleHeaderCell
									label={header}
									onClick={() => setIsSellDirection(!isSellDirection)}
								/>
							) : (
								header
							)}
						</TableV2.HeaderCell>
					))}
				</TableV2.HeaderRow>
			}
			customMiddleContentWrapper
			tableContentMiddle={
				<div>
					{isMobileScreenSize && !props.noExpandAllButton && (
						<div className="flex w-full items-center justify-end p-3">
							<ExpandAllButton
								areAllItemsExpanded={areAllItemsExpanded}
								totalCount={currentSwapsHistory?.length ?? 0}
								onExpandAll={handleExpandAllItems}
								onCollapseAll={handleCollapseAllItems}
							/>
						</div>
					)}

					<div
						className={cm(
							MOBILE_TABLE_CARD_LIST_CLASSNAME,
							props.tableContentWrapperClassName
						)}
					>
						{currentSwapsHistory?.map((swapRecord) =>
							isMobileScreenSize ? (
								<SwapHistoryTableMobileCard
									key={swapRecord.txSig}
									swap={swapRecord}
									displayDatesAsTimestamp={displayDatesAsTimestamp}
									isSellDirection={isSellDirection}
									areAllItemsExpanded={areAllItemsExpanded}
									handleItemExpandedChange={handleItemExpandedChange}
									toggleDateDisplayType={toggleDateDisplayType}
									toggleSellDirection={() =>
										setIsSellDirection(!isSellDirection)
									}
								/>
							) : (
								<SwapHistoryTableRow
									key={swapRecord.txSig}
									swap={swapRecord}
									tableGrid={TABLE_GRID}
									displayDatesAsTimestamp={displayDatesAsTimestamp}
									isSellDirection={isSellDirection}
								/>
							)
						)}
					</div>
				</div>
			}
		/>
	);
};

export const V2SwapHistoryTable = (
	props: Pick<
		V2SwapHistoryTableDisplayProps,
		'tableContentWrapperClassName' | 'noExpandAllButton' | 'isLive'
	>
) => {
	const tableState = useSwapsHistory();

	return <V2SwapHistoryTableDisplay {...tableState} {...props} />;
};
